import { useMemo } from 'react';
import { useCentralizedProviderData } from '@crehana/auth';
import { useHasPermissions } from "./use-has-permissions";
import { usePathname } from "./use-pathname";

// Organizations not allowed to access Hana AI (temporary hotfix)
// In case of doubts, contact Alejandra Hernandez and Alejandra Guevara
var disallowedOrganizations = ['universidad-comex'];

// ===== PATH UTILITIES =====
var getPathSegments = function getPathSegments(pathname) {
  var cleanPath = pathname.split('?')[0].split('#')[0];
  return cleanPath.split('/').filter(Boolean);
};
var isAdminPath = function isAdminPath(segments) {
  return segments.some(function (s) {
    return s === 'admin' || s === 'panel';
  });
};

// ===== USER PERMISSIONS HOOK =====
export var useUserPermissions = function useUserPermissions() {
  var _me$security2, _me$security2$learnin, _me$security3, _me$security3$learnin, _me$security4, _me$security5, _me$security5$learnin;
  var _useCentralizedProvid = useCentralizedProviderData(),
    me = _useCentralizedProvid.me;
  var pathname = usePathname();

  // Path detection
  var segments = getPathSegments(pathname);
  var isAdminPathResult = isAdminPath(segments);
  var viewMode = isAdminPathResult ? 'admin' : 'end-user';

  // User data extraction
  var learningOrganizationId = useMemo(function () {
    var _me$security, _me$security$learning;
    var orgId = me === null || me === void 0 ? void 0 : (_me$security = me.security) === null || _me$security === void 0 ? void 0 : (_me$security$learning = _me$security.learningSession) === null || _me$security$learning === void 0 ? void 0 : _me$security$learning.organizationId;
    return orgId != null ? Number(orgId) : undefined;
  }, [me === null || me === void 0 ? void 0 : (_me$security2 = me.security) === null || _me$security2 === void 0 ? void 0 : (_me$security2$learnin = _me$security2.learningSession) === null || _me$security2$learnin === void 0 ? void 0 : _me$security2$learnin.organizationId]);
  var slug = me === null || me === void 0 ? void 0 : (_me$security3 = me.security) === null || _me$security3 === void 0 ? void 0 : (_me$security3$learnin = _me$security3.learningSession) === null || _me$security3$learnin === void 0 ? void 0 : _me$security3$learnin.organizationSlug;
  var applicationPermissions = (me === null || me === void 0 ? void 0 : (_me$security4 = me.security) === null || _me$security4 === void 0 ? void 0 : _me$security4.applicationPermissions) || [];

  // User validation
  var isUserValid = Boolean(me === null || me === void 0 ? void 0 : (_me$security5 = me.security) === null || _me$security5 === void 0 ? void 0 : (_me$security5$learnin = _me$security5.learningSession) === null || _me$security5$learnin === void 0 ? void 0 : _me$security5$learnin.organizationId);
  var isUserSessionValid = isUserValid;

  // Permission checks
  var _useHasPermissions = useHasPermissions(),
    hasHanaAccess = _useHasPermissions.hasHanaAccess,
    hasPermissions = _useHasPermissions.hasPermissions;
  var hasHanaAccessResult = hasHanaAccess();
  var hasPermissionsResult = hasPermissions(['HanaAdminUserView', 'HanaUserView'], {
    operator: 'OR'
  });
  var canAccessHanaAI = useMemo(function () {
    // Check if the organization is in the disallowed list
    if (slug && disallowedOrganizations.includes(slug)) {
      return false;
    }
    return isUserValid && (hasPermissionsResult || hasHanaAccessResult);
  }, [isUserValid, hasPermissionsResult, hasHanaAccessResult, slug]);
  return {
    // Path & View Mode
    viewMode: viewMode,
    isAdminPath: isAdminPathResult,
    pathname: pathname,
    // User Data
    me: me,
    learningOrganizationId: learningOrganizationId,
    slug: slug,
    // User Validation
    isUserValid: isUserValid,
    isUserSessionValid: isUserSessionValid,
    applicationPermissions: applicationPermissions,
    // Access Control
    canAccessHanaAI: canAccessHanaAI
  };
};